import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  TextField, 
  Button, 
  Paper, 
  CircularProgress, 
  Alert, 
  Card, 
  CardContent, 
  Chip,
  Divider,
  Grid,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  FormControlLabel,
  Switch,
  FormGroup,
  Checkbox
} from '@mui/material';
import { 
  Add as AddIcon, 
  Delete as DeleteIcon, 
  Edit as EditIcon,
  AddCircleOutline as AddMedicationIcon,
  Sort as SortIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon
} from '@mui/icons-material';
import { 
  fetchUserPrescriptions, 
  fetchPrescriptionById, 
  createPrescription, 
  updatePrescription, 
  deletePrescription,
  fetchUserMedications,
  fetchAllPrescriptionRequests,
  enrichPrescriptionWithMedicationDetails,
  fetchAllPrescriptions
} from '../services/healinApi';

const PrescriptionTest = () => {
  const [userId, setUserId] = useState('');
  const [prescriptions, setPrescriptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [selectedPrescription, setSelectedPrescription] = useState(null);
  const [availableMedications, setAvailableMedications] = useState([]);
  const [fetchAllMode, setFetchAllMode] = useState(false);
  
  // Sort state
  const [sortBy, setSortBy] = useState('none'); // 'none', 'date-asc', 'date-desc', 'status'
  const [sortedPrescriptions, setSortedPrescriptions] = useState([]);
  
  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState('create'); // 'create' or 'edit'
  const [medicationDialogOpen, setMedicationDialogOpen] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    status: 'Pending',
    comments: '',
    medications: [],
    source: 'manual'
  });

  const [selectedMedication, setSelectedMedication] = useState('');
  const [useNewAPI, setUseNewAPI] = useState(true);
  const [includeMedicationData, setIncludeMedicationData] = useState(false);
  const [selectedStatuses, setSelectedStatuses] = useState({
    Pending: true,
    Claimed: false,
    Issued: false
  });

  const availableStatuses = ['Pending', 'Claimed', 'Issued'];

  // Sort function
  const sortPrescriptions = (prescriptionsToSort, sortType) => {
    if (!prescriptionsToSort || prescriptionsToSort.length === 0) return [];
    
    const sorted = [...prescriptionsToSort];
    
    switch (sortType) {
      case 'date-asc':
        return sorted.sort((a, b) => {
          const dateA = new Date(a.requestedAt || a.createdAt || 0);
          const dateB = new Date(b.requestedAt || b.createdAt || 0);
          return dateA - dateB;
        });
      case 'date-desc':
        return sorted.sort((a, b) => {
          const dateA = new Date(a.requestedAt || a.createdAt || 0);
          const dateB = new Date(b.requestedAt || b.createdAt || 0);
          return dateB - dateA;
        });
      case 'status':
        return sorted.sort((a, b) => {
          const statusA = (a.status || '').toLowerCase();
          const statusB = (b.status || '').toLowerCase();
          return statusA.localeCompare(statusB);
        });
      case 'none':
      default:
        return sorted;
    }
  };

  // Handle sort change
  const handleSortChange = (newSortType) => {
    setSortBy(newSortType);
    const sorted = sortPrescriptions(prescriptions, newSortType);
    setSortedPrescriptions(sorted);
  };

  // Update sorted prescriptions when prescriptions change
  React.useEffect(() => {
    const sorted = sortPrescriptions(prescriptions, sortBy);
    setSortedPrescriptions(sorted);
  }, [prescriptions, sortBy]);

  // Get sort button text and icon
  const getSortButtonText = () => {
    switch (sortBy) {
      case 'date-asc':
        return 'Date (Oldest First)';
      case 'date-desc':
        return 'Date (Newest First)';
      case 'status':
        return 'Status (A-Z)';
      case 'none':
      default:
        return 'No Sort';
    }
  };

  const getSortIcon = () => {
    switch (sortBy) {
      case 'date-asc':
        return <ArrowUpwardIcon />;
      case 'date-desc':
        return <ArrowDownwardIcon />;
      case 'status':
        return <SortIcon />;
      case 'none':
      default:
        return <SortIcon />;
    }
  };

  // Cycle through sort options
  const cycleSortOption = () => {
    const sortOptions = ['none', 'date-desc', 'date-asc', 'status'];
    const currentIndex = sortOptions.indexOf(sortBy);
    const nextIndex = (currentIndex + 1) % sortOptions.length;
    handleSortChange(sortOptions[nextIndex]);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const resetForm = () => {
    setFormData({
      status: 'Pending',
      comments: '',
      medications: [],
      source: 'manual'
    });
    setSelectedMedication('');
  };

  const openCreateDialog = async () => {
    resetForm();
    setDialogMode('create');
    await fetchAvailableMedications();
    setDialogOpen(true);
  };

  const openEditDialog = async (prescription) => {
    setSelectedPrescription(prescription);
    setFormData({
      status: prescription.status || 'Pending',
      comments: prescription.comments || '',
      medications: prescription.medications || [],
      source: prescription.source || 'manual'
    });
    await fetchAvailableMedications();
    setDialogMode('edit');
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setSelectedPrescription(null);
  };

  const openMedicationDialog = () => {
    setMedicationDialogOpen(true);
  };

  const closeMedicationDialog = () => {
    setMedicationDialogOpen(false);
  };

  const addMedicationToForm = () => {
    if (!selectedMedication) return;
    
    const medicationToAdd = availableMedications.find(
      med => med.medicationId === selectedMedication
    );
    
    if (medicationToAdd) {
      const updatedMedications = [...formData.medications];
      
      // Check if medication already exists in the list
      const exists = updatedMedications.some(
        med => med.medicationId === medicationToAdd.medicationId
      );
      
      if (!exists) {
        updatedMedications.push({
          medicationId: medicationToAdd.medicationId,
          medicationName: medicationToAdd.medicationName,
          dosage: medicationToAdd.dosage,
          instructions: medicationToAdd.posology
        });
        
        setFormData(prev => ({
          ...prev,
          medications: updatedMedications
        }));
      }
    }
    
    setSelectedMedication('');
    closeMedicationDialog();
  };

  const removeMedicationFromForm = (medicationId) => {
    const updatedMedications = formData.medications.filter(
      med => med.medicationId !== medicationId
    );
    
    setFormData(prev => ({
      ...prev,
      medications: updatedMedications
    }));
  };

  const fetchAvailableMedications = async () => {
    if (!userId) return;
    
    try {
      const medications = await fetchUserMedications(userId);
      setAvailableMedications(medications);
    } catch (err) {
      console.error('Error fetching medications:', err);
    }
  };

  const handleStatusChange = (status) => {
    setSelectedStatuses(prev => ({
      ...prev,
      [status]: !prev[status]
    }));
  };

  const getSelectedStatuses = () => {
    return availableStatuses.filter(status => selectedStatuses[status]);
  };

  // Helper function to format status for display (now just returns as-is since we store them properly)
  const formatStatusLabel = (status) => {
    if (!status) return 'Unknown';
    return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
  };

  // Helper function to get status color
  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'pending':
        return 'warning';
      case 'claimed':
        return 'info';
      case 'issued':
        return 'success';
      default:
        return 'default';
    }
  };

  const handleSubmit = async () => {
    if (!userId) {
      setError('User ID is required');
      return;
    }

    if (formData.medications.length === 0) {
      setError('At least one medication is required');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      if (dialogMode === 'create') {
        const newMedication = await createPrescription({
          userId,
          ...formData
        });
        setSuccess('Prescription created successfully');
        await fetchPrescriptions();
      } else if (dialogMode === 'edit') {
        const updatedMedication = await updatePrescription({
          userId,
          requestId: selectedPrescription.requestId,
          status: formData.status,
          comments: formData.comments,
          source: formData.source
        });
        setSuccess('Prescription updated successfully');
        await fetchPrescriptions();
      }
      closeDialog();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (requestId) => {
    if (!userId) {
      setError('User ID is required');
      return;
    }

    if (!window.confirm('Are you sure you want to delete this prescription?')) {
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      await deletePrescription(userId, requestId);
      setSuccess('Prescription deleted successfully');
      await fetchPrescriptions();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchPrescriptions = async () => {
    if (!useNewAPI && !userId.trim()) {
      setError('Please enter a User ID when using the old API');
      return;
    }

    if (useNewAPI && getSelectedStatuses().length === 0) {
      setError('Please select at least one status when using the new API');
      return;
    }

    try {
      setLoading(true);
      setError('');
      
      let prescriptionsData;
      
      if (useNewAPI) {
        // Use new batch API
        const apiOptions = {
          statuses: getSelectedStatuses(),
          includeMedicationData
        };
        
        console.log('=== FRONTEND DEBUG ===');
        console.log('Using new batch API with options:', apiOptions);
        console.log('Selected statuses from state:', selectedStatuses);
        console.log('Filtered selected statuses:', getSelectedStatuses());
        
        prescriptionsData = await fetchAllPrescriptions(apiOptions);
        
        console.log('=== FRONTEND RESPONSE DEBUG ===');
        console.log('Received prescriptions data:', prescriptionsData);
        console.log('Type of prescriptionsData:', typeof prescriptionsData);
        console.log('Is array?', Array.isArray(prescriptionsData));
        console.log('Length:', prescriptionsData?.length);
        
        if (prescriptionsData && prescriptionsData.length > 0) {
          console.log('First prescription sample:', prescriptionsData[0]);
          console.log('Medications in first prescription:', prescriptionsData[0]?.medications);
          console.log('MedicationsDetail in first prescription:', prescriptionsData[0]?.medicationsDetail);
        }
      } else {
        // Use old API (for comparison)
        console.log('Using old API for user:', userId);
        prescriptionsData = await fetchAllPrescriptionRequests(userId);
      }

      console.log('Final prescriptions to display:', prescriptionsData);
      setPrescriptions(prescriptionsData);
      
      if (prescriptionsData.length === 0) {
        setError('No prescriptions found with the selected criteria. Check console for detailed logs.');
      }
    } catch (err) {
      console.error('=== FRONTEND ERROR DEBUG ===');
      console.error('Error fetching prescriptions:', err);
      console.error('Error stack:', err.stack);
      setError(err.message || 'An error occurred while fetching prescriptions');
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = async (requestId) => {
    if (!userId && !fetchAllMode) {
      setError('User ID is required for viewing details');
      return;
    }
    
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      let data;
      if (fetchAllMode) {
        // For all prescriptions mode, find the details from the already fetched prescriptions
        data = prescriptions.find(p => p.requestId === requestId);
        if (!data) {
          throw new Error("Could not find prescription details");
        }
      } else {
        data = await fetchPrescriptionById(userId, requestId);
      }
      alert(JSON.stringify(data, null, 2));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Format date string
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleString();
    } catch (e) {
      return dateString;
    }
  };

  const testSimpleAPICall = async () => {
    try {
      setLoading(true);
      setError('');
      
      console.log('=== TESTING SIMPLE API CALL ===');
      
      // Make a direct fetch call to see what we get
      const url = 'https://8uzk12sdzc.execute-api.eu-west-1.amazonaws.com/prod/prescriptions?all=true';
      console.log('Testing URL:', url);
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': 'healin-2024-api-key'
        }
      });
      
      console.log('Simple API Response Status:', response.status);
      console.log('Simple API Response Headers:', Object.fromEntries(response.headers.entries()));
      
      const responseText = await response.text();
      console.log('Simple API Raw Response:', responseText);
      
      if (response.ok) {
        const data = JSON.parse(responseText);
        console.log('Simple API Parsed Data:', data);
        console.log('Items count:', data.items?.length || 0);
        
        if (data.items && data.items.length > 0) {
          console.log('Sample item from simple call:', data.items[0]);
        }
        
        setPrescriptions(data.items || []);
      } else {
        setError(`API Error: ${response.status} - ${responseText}`);
      }
      
    } catch (err) {
      console.error('Simple API call error:', err);
      setError(`Test failed: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const testStatusFilter = async () => {
    try {
      setLoading(true);
      setError('');
      
      console.log('=== TESTING STATUS FILTER ===');
      
      // Test different status filter combinations
      const testCases = [
        { statuses: 'Pending', description: 'Single status: Pending' },
        { statuses: 'Pending,Claimed,Issued', description: 'Multiple statuses: Pending,Claimed,Issued' },
        { statuses: '', description: 'Empty status (should return all)' }
      ];
      
      for (const testCase of testCases) {
        console.log(`\n--- Testing: ${testCase.description} ---`);
        
        let url = 'https://8uzk12sdzc.execute-api.eu-west-1.amazonaws.com/prod/prescriptions?all=true';
        if (testCase.statuses) {
          url += `&statuses=${testCase.statuses}`;
        }
        console.log('Test URL:', url);
        
        const response = await fetch(url, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'x-api-key': 'healin-2024-api-key'
          }
        });
        
        console.log(`Response Status for ${testCase.description}:`, response.status);
        
        if (response.ok) {
          const data = await response.json();
          console.log(`Items returned for ${testCase.description}:`, data.items?.length || 0);
          
          if (data.items && data.items.length > 0) {
            // Show unique statuses found
            const uniqueStatuses = [...new Set(data.items.map(item => item.status))];
            console.log('Unique statuses found:', uniqueStatuses);
            console.log('Sample items:', data.items.slice(0, 2).map(item => ({
              requestId: item.requestId,
              status: item.status,
              medications: item.medications?.length || 0
            })));
          }
        } else {
          const errorText = await response.text();
          console.error(`Error for ${testCase.description}:`, errorText);
        }
      }
      
      // Test includeMedicationData with no status filter (should work)
      console.log('\n--- Testing includeMedicationData (no status filter) ---');
      const medUrl = 'https://8uzk12sdzc.execute-api.eu-west-1.amazonaws.com/prod/prescriptions?all=true&includeMedicationData=true';
      console.log('Medication test URL:', medUrl);
      
      const medResponse = await fetch(medUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': 'healin-2024-api-key'
        }
      });
      
      if (medResponse.ok) {
        const medData = await medResponse.json();
        console.log('Items with medication data (no filter):', medData.items?.length || 0);
        
        if (medData.items && medData.items.length > 0) {
          console.log('Sample item with medication data:', medData.items[0]);
          console.log('Has medicationsDetail?', !!medData.items[0].medicationsDetail);
          console.log('MedicationsDetail length:', medData.items[0].medicationsDetail?.length || 0);
          
          if (medData.items[0].medicationsDetail && medData.items[0].medicationsDetail.length > 0) {
            console.log('Sample medication detail:', medData.items[0].medicationsDetail[0]);
          }
        }
      }
      
      // Test both status filter AND medication data
      console.log('\n--- Testing Status Filter + Medication Data ---');
      const combinedUrl = 'https://8uzk12sdzc.execute-api.eu-west-1.amazonaws.com/prod/prescriptions?all=true&statuses=Pending&includeMedicationData=true';
      console.log('Combined test URL:', combinedUrl);
      
      const combinedResponse = await fetch(combinedUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': 'healin-2024-api-key'
        }
      });
      
      if (combinedResponse.ok) {
        const combinedData = await combinedResponse.json();
        console.log('Items with status filter + medication data:', combinedData.items?.length || 0);
        
        if (combinedData.items && combinedData.items.length > 0) {
          console.log('Combined test sample:', {
            requestId: combinedData.items[0].requestId,
            status: combinedData.items[0].status,
            hasMedicationsDetail: !!combinedData.items[0].medicationsDetail,
            medicationsDetailCount: combinedData.items[0].medicationsDetail?.length || 0
          });
        }
        
        // Use this data for the display
        setPrescriptions(combinedData.items || []);
      }
      
    } catch (err) {
      console.error('Status filter test error:', err);
      setError(`Test failed: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Prescription API Test
      </Typography>
      
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          API Configuration
        </Typography>
        
        <FormControlLabel
          control={
            <Switch
              checked={useNewAPI}
              onChange={(e) => setUseNewAPI(e.target.checked)}
              color="primary"
            />
          }
          label={useNewAPI ? "Using New Batch API" : "Using Old API"}
          sx={{ mb: 2 }}
        />

        {useNewAPI ? (
          <Box>
            <Typography variant="subtitle1" gutterBottom>
              Status Filters
            </Typography>
            <FormGroup row sx={{ mb: 2 }}>
              {availableStatuses.map((status) => (
                <FormControlLabel
                  key={status}
                  control={
                    <Checkbox
                      checked={selectedStatuses[status]}
                      onChange={() => handleStatusChange(status)}
                      color="primary"
                    />
                  }
                  label={formatStatusLabel(status)}
                />
              ))}
            </FormGroup>

            <FormControlLabel
              control={
                <Switch
                  checked={includeMedicationData}
                  onChange={(e) => setIncludeMedicationData(e.target.checked)}
                  color="primary"
                  disabled={false}
                />
              }
              label="Include Medication Data"
              sx={{ mb: 2 }}
            />
          </Box>
        ) : (
          <TextField
            fullWidth
            label="User ID"
            value={userId}
            onChange={(e) => setUserId(e.target.value)}
            margin="normal"
            placeholder="Enter User ID to fetch prescriptions"
          />
        )}

        <Button
          variant="contained"
          onClick={fetchPrescriptions}
          disabled={loading}
          sx={{ mt: 2, mr: 2 }}
        >
          {loading ? <CircularProgress size={24} /> : 'Fetch Prescriptions'}
        </Button>

        <Button
          variant="outlined"
          onClick={testSimpleAPICall}
          disabled={loading}
          sx={{ mt: 2, mr: 2 }}
        >
          Test Simple API Call
        </Button>

        <Button
          variant="outlined"
          color="secondary"
          onClick={testStatusFilter}
          disabled={loading}
          sx={{ mt: 2 }}
        >
          Test Status Filter
        </Button>
      </Paper>

      {prescriptions.length > 0 && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="h6">
              Sort Options
            </Typography>
            <Button
              variant="outlined"
              onClick={cycleSortOption}
              startIcon={getSortIcon()}
              sx={{ minWidth: 200 }}
            >
              {getSortButtonText()}
            </Button>
            <Typography variant="body2" color="text.secondary">
              Click to cycle through sort options
            </Typography>
          </Box>
        </Paper>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {prescriptions.length > 0 && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Results ({prescriptions.length} prescriptions)
            {sortBy !== 'none' && (
              <Chip 
                label={`Sorted by: ${getSortButtonText()}`} 
                size="small" 
                color="primary" 
                sx={{ ml: 2 }}
              />
            )}
          </Typography>
          
          {sortedPrescriptions.map((prescription, index) => (
            <Card key={prescription.requestId || index} sx={{ mb: 2 }}>
              <CardContent>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="h6">
                      Request ID: {prescription.requestId}
                    </Typography>
                    <Typography color="text.secondary">
                      User ID: {prescription.userId}
                    </Typography>
                    <Typography color="text.secondary" component="span" sx={{ display: 'flex', alignItems: 'center' }}>
                      Status: 
                      <Chip 
                        label={formatStatusLabel(prescription.status)} 
                        color={getStatusColor(prescription.status)}
                        size="small"
                        sx={{ ml: 1 }}
                      />
                    </Typography>
                    <Typography color="text.secondary">
                      Requested: {formatDate(prescription.requestedAt)}
                    </Typography>
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle1" gutterBottom>
                      Medications ({prescription.medications?.length || 0})
                    </Typography>
                    
                    {prescription.medicationsDetail && prescription.medicationsDetail.length > 0 ? (
                      // Display enriched medication data
                      <Box>
                        {prescription.medicationsDetail.map((med, medIndex) => {
                          // Handle null medication details
                          if (!med) {
                            return (
                              <Box key={medIndex} sx={{ mb: 1, p: 1, bgcolor: 'grey.100', borderRadius: 1 }}>
                                <Typography variant="body2" color="text.secondary" fontStyle="italic">
                                  Medication details not available
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  ID: {prescription.medications?.[medIndex] || 'Unknown'}
                                </Typography>
                              </Box>
                            );
                          }
                          
                          return (
                            <Box key={med.medicationId || medIndex} sx={{ mb: 1, p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
                              <Typography variant="body2" fontWeight="bold">
                                {med.medicationName || med.name || 'Unknown Medication'}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                Dosage: {med.dosage || 'Not specified'}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                Instructions: {med.posology || 'Not specified'}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                Type: {med.type || 'Not specified'}
                              </Typography>
                              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                                ID: {med.medicationId || 'Unknown'}
                              </Typography>
                            </Box>
                          );
                        })}
                      </Box>
                    ) : (
                      // Display medication IDs only
                      <Box>
                        {prescription.medications?.map((medId, medIndex) => (
                          <Chip key={medIndex} label={medId} size="small" sx={{ mr: 1, mb: 1 }} />
                        ))}
                      </Box>
                    )}
                  </Grid>
                </Grid>
                
                {prescription.comments && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2">Comments:</Typography>
                    <Typography variant="body2">{prescription.comments}</Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          ))}
        </Paper>
      )}
    </Box>
  );
};

export default PrescriptionTest; 